import React, { useState } from 'react';
import { Calculator } from '../../../types';
import { Icon } from '../../Icon';
import { CalculatorShell } from '../CalculatorShell';
import { maskNumber, maskInteger, parseBrazilianNumber } from '../../../utils/formatUtils';

interface DecimoTerceiroCalculatorProps {
  calculator: Calculator;
  onBack: () => void;
}

const DecimoTerceiroCalculator = () => {
  const [salary, setSalary] = useState<string>('');
  const [monthsWorked, setMonthsWorked] = useState<string>('');
  const [dependents, setDependents] = useState<string>('');
  const [resultState, setResultState] = useState<any>(null);

  const calculate = () => {
    const sal = parseFloat(parseBrazilianNumber(salary)) || 0;
    const months = parseInt(parseBrazilianNumber(monthsWorked)) || 0;
    const deps = parseInt(parseBrazilianNumber(dependents)) || 0;

    if (sal <= 0 || months <= 0) return;

    // Cálculo do 13º proporcional
    const proportionalValue = (sal / 12) * months;

    // Dedução do INSS sobre 13º (mesma tabela do salário)
    let inssDiscount = 0;
    if (proportionalValue <= 1412.00) {
      inssDiscount = proportionalValue * 0.075;
    } else if (proportionalValue <= 2666.68) {
      inssDiscount = (1412.00 * 0.075) + ((proportionalValue - 1412.00) * 0.09);
    } else if (proportionalValue <= 4000.03) {
      inssDiscount = (1412.00 * 0.075) + ((2666.68 - 1412.00) * 0.09) + ((proportionalValue - 2666.68) * 0.12);
    } else if (proportionalValue <= 7786.02) {
      inssDiscount = (1412.00 * 0.075) + ((2666.68 - 1412.00) * 0.09) + ((4000.03 - 2666.68) * 0.12) + ((proportionalValue - 4000.03) * 0.14);
    } else {
      inssDiscount = 908.85; // Teto INSS
    }

    // Base de cálculo do IRPF sobre 13º
    const baseIR = proportionalValue - inssDiscount - (deps * 189.59);

    // Dedução simplificada (mais vantajosa para 13º)
    const baseSimplified = proportionalValue - inssDiscount - 528.00;
    const baseEfetiva = Math.max(0, Math.min(baseIR, baseSimplified));
    const isSimplificado = baseSimplified < baseIR;

    // Cálculo do IRPF sobre 13º
    let irpfDiscount = 0;
    if (baseEfetiva <= 2112.00) {
      irpfDiscount = 0;
    } else if (baseEfetiva <= 2826.65) {
      irpfDiscount = (baseEfetiva * 0.075) - 158.40;
    } else if (baseEfetiva <= 3751.05) {
      irpfDiscount = (baseEfetiva * 0.15) - 370.40;
    } else if (baseEfetiva <= 4664.68) {
      irpfDiscount = (baseEfetiva * 0.225) - 651.73;
    } else {
      irpfDiscount = (baseEfetiva * 0.275) - 884.96;
    }
    irpfDiscount = Math.max(0, irpfDiscount);

    const totalDiscounts = inssDiscount + irpfDiscount;
    const netValue = proportionalValue - totalDiscounts;

    // Simulação da primeira parcela (adiantamento)
    const firstInstallment = netValue / 2;
    const secondInstallment = netValue - firstInstallment;

    setResultState({
      salary: sal,
      monthsWorked: months,
      proportionalValue,
      inssDiscount,
      irpfDiscount,
      totalDiscounts,
      netValue,
      firstInstallment,
      secondInstallment,
      method: isSimplificado ? 'Simplificado' : 'Completo'
    });
  };

  const hasResult = resultState !== null;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-green-50 to-slate-50 rounded-full border border-green-200 mb-4">
          <span className="text-2xl">💰</span>
          <span className="text-sm font-black text-theme-text uppercase tracking-wider">13º Salário</span>
        </div>
        <p className="text-theme-muted text-sm">Cálculo proporcional com descontos obrigatórios</p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <h3 className="text-lg font-bold text-theme-text mb-6">Informações para Cálculo</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Salário (R$)</label>
            <div className="relative">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-xl font-black text-theme-muted">R$</span>
              <input
                type="text"
                value={salary}
                onChange={e => setSalary(maskNumber(e.target.value))}
                placeholder="Ex: 5.000,00"
                className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400 pl-12"
              />
            </div>
            <div className="text-xs text-theme-muted mt-2">Salário mensal base</div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Meses Trabalhados</label>
            <input
              type="text"
              value={monthsWorked}
              onChange={e => setMonthsWorked(maskInteger(e.target.value))}
              placeholder="Ex: 12"
              className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400"
            />
            <div className="text-xs text-theme-muted mt-2">Período no ano (máx 12 meses)</div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Dependentes</label>
            <input
              type="text"
              value={dependents}
              onChange={e => setDependents(maskInteger(e.target.value))}
              placeholder="Ex: 0"
              className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400"
            />
            <div className="text-xs text-theme-muted mt-2">Número de dependentes para IRPF</div>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular 13º Salário
          </button>
        </div>
      </div>

      {hasResult && (
        <div className="relative">
          <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 opacity-10 blur-3xl" />
          <div className="relative p-10 rounded-[2.5rem] text-center shadow-2xl border-2 bg-gradient-to-br from-green-500 to-emerald-500 border-green-400 text-white">
            <div className="flex justify-center mb-5">
              <div className="w-20 h-20 rounded-full bg-theme-result/20 backdrop-blur-sm flex items-center justify-center text-4xl shadow-xl">💰</div>
            </div>

            <div className="mb-6">
              <p className="text-xs font-black uppercase tracking-widest opacity-70 mb-2">13º Proporcional Bruto</p>
              <p className="text-4xl font-black mb-2">R$ {resultState.proportionalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              <div className="w-20 h-1 bg-theme-result/30 rounded-full mx-auto" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Desconto INSS</p>
                <p className="text-2xl font-black">R$ {resultState.inssDiscount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">contribuição previdenciária</p>
              </div>
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Desconto IRPF</p>
                <p className="text-2xl font-black">R$ {resultState.irpfDiscount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">{resultState.method}</p>
              </div>
            </div>

            <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4 mb-8">
              <p className="text-xs font-black uppercase opacity-70 mb-1">Valor Líquido</p>
              <p className="text-3xl font-black">R$ {resultState.netValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs opacity-70 mt-1">valor a receber</p>
            </div>

            <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4 border border-white/20">
              <p className="text-xs font-black uppercase opacity-70 mb-2">💸 Simulação Adiantamento</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span>1ª Parcela (férias):</span>
                  <span className="font-bold">R$ {resultState.firstInstallment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span>2ª Parcela (dezembro):</span>
                  <span className="font-bold">R$ {resultState.secondInstallment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
              <p className="text-xs opacity-70 mt-2">Opção de adiantar metade nas férias</p>
            </div>
          </div>
        </div>
      )}

      {!hasResult && (
        <div className="h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-[2.5rem] border-2 border-dashed border-theme-border text-theme-muted p-10 text-center">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center text-2xl mb-3">💰</div>
          <p className="font-bold text-base">Preencha os dados para calcular o 13º</p>
        </div>
      )}
    </div>
  );
};

export default DecimoTerceiroCalculator;
